// frontend/src/components/RichTextEditor.tsx
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import ImageExtension from '@tiptap/extension-image';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Strikethrough, Code, List, ListOrdered, MessageSquareQuote, Minus, Pilcrow, Heading1, Heading2, Heading3, Link2 as LinkIcon, Eraser, Image as ImageIcon, Superscript as SuperscriptIcon, Subscript as SubscriptIcon, UploadCloud,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Loader2
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';

import { uploadArticleImage } from '@/api/articleService';
import { uploadProductImage } from '@/api/productService';

const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

const CustomImage = ImageExtension.extend({
  name: 'customImage',
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      ...this.parent?.(),
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      'data-align': {
        default: null,
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes['data-align']) {
            return {};
          }
          return { 'data-align': attributes['data-align'] };
        },
        parseHTML: (element: HTMLElement) => ({
          'data-align': element.getAttribute('data-align'),
        }),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }: { node: ProseMirrorNode, HTMLAttributes: Record<string, any> }) {
    const align = node.attrs['data-align'] as string | null;
    let currentClassName = (HTMLAttributes.class as string) || '';
    currentClassName = currentClassName.replace(/\bimg-align-(left|center|right)\b/g, '').trim();

    let alignClass = '';
    if (align === 'center') {
      alignClass = 'img-align-center';
    } else if (align === 'right') {
      alignClass = 'img-align-right';
    } else if (align === 'left') {
      alignClass = 'img-align-left';
    }

    const finalClassName = `${currentClassName} ${alignClass}`.trim();
    const finalAttrs: Record<string, any> = { ...HTMLAttributes };

    if (finalClassName) {
      finalAttrs.class = finalClassName;
    } else {
      delete finalAttrs.class;
    }
    
    return ['img', finalAttrs];
  },

  parseHTML() {
    return [
      {
        tag: 'img[src]:not([src^="data:"])',
        getAttrs: (domNode: HTMLElement) => {
          const element = domNode as HTMLImageElement;
          let align = element.getAttribute('data-align');
          
          if (!align) { 
            align =
              element.classList.contains('img-align-center') ? 'center' :
              element.classList.contains('img-align-right') ? 'right' :
              element.classList.contains('img-align-left') ? 'left' :
              null;
          }

          return {
            src: element.getAttribute('src'),
            alt: element.getAttribute('alt'),
            title: element.getAttribute('title'),
            'data-align': align,
          };
        },
      },
    ];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageAlignment: (alignment: 'left' | 'center' | 'right' | null) => 
        ({ commands, editor }: any) => { 
          if (!editor.isActive(this.name)) {
            return false; 
          }
          return commands.updateAttributes(this.name, { 'data-align': alignment });
        },
    };
  },
});

const MenuBar = ({ editor }: { editor: any | null }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const location = useLocation();

  if (!editor) {
    return null;
  }

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImageByUrl = useCallback(() => {
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const handleFileSelectAndUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isProductPage = location.pathname.includes('/admin/products');
    const uploadService = isProductPage ? uploadProductImage : uploadArticleImage;

    setIsUploading(true);
    try {
      const response = await uploadService(file);
      if (response && response.image_url) {
        const fullImageUrl = `${backendBaseUrl}/static/${response.image_url}`;
        editor.chain().focus().setImage({ src: fullImageUrl, alt: file.name }).run();
      } else {
        alert("Image uploaded, but no URL was returned.");
      }
    } catch (error) {
      console.error("Failed to upload image for editor:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="border border-input rounded-t-md p-2 flex flex-wrap gap-1 bg-muted/30">
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active bg-primary/20' : ''} title="Bold"> <Bold className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active bg-primary/20' : ''} title="Italic"> <Italic className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active bg-primary/20' : ''} title="Strikethrough"> <Strikethrough className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleCode().run()} className={editor.isActive('code') ? 'is-active bg-primary/20' : ''} title="Inline Code"> <Code className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleSuperscript().run()} className={editor.isActive('superscript') ? 'is-active bg-primary/20' : ''} title="Superscript"> <SuperscriptIcon className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleSubscript().run()} className={editor.isActive('subscript') ? 'is-active bg-primary/20' : ''} title="Subscript"> <SubscriptIcon className="h-4 w-4" /> </Button>

      <span className="border-r border-border mx-1"></span>
      
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setTextAlign('left').run()} className={editor.isActive({ textAlign: 'left' }) ? 'is-active bg-primary/20' : ''} title="Align Text Left"> <AlignLeft className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setTextAlign('center').run()} className={editor.isActive({ textAlign: 'center' }) ? 'is-active bg-primary/20' : ''} title="Align Text Center"> <AlignCenter className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setTextAlign('right').run()} className={editor.isActive({ textAlign: 'right' }) ? 'is-active bg-primary/20' : ''} title="Align Text Right"> <AlignRight className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setTextAlign('justify').run()} className={editor.isActive({ textAlign: 'justify' }) ? 'is-active bg-primary/20' : ''} title="Align Text Justify"> <AlignJustify className="h-4 w-4" /> </Button>
      
      <span className="border-r border-border mx-1"></span>

      <Button type="button" variant="outline" size="sm" onClick={() => editor.commands.setImageAlignment('left')} disabled={!editor.isActive('customImage')} className={editor.isActive('customImage', { 'data-align': 'left' }) ? 'is-active bg-primary/20' : ''} title="Align Image Left" > <ImageIcon className="h-4 w-4 mr-1" /> L </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.commands.setImageAlignment('center')} disabled={!editor.isActive('customImage')} className={editor.isActive('customImage', { 'data-align': 'center' }) ? 'is-active bg-primary/20' : ''} title="Align Image Center" > <ImageIcon className="h-4 w-4 mr-1" /> C </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.commands.setImageAlignment('right')} disabled={!editor.isActive('customImage')} className={editor.isActive('customImage', { 'data-align': 'right' }) ? 'is-active bg-primary/20' : ''} title="Align Image Right" > <ImageIcon className="h-4 w-4 mr-1" /> R </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.commands.setImageAlignment(null)} disabled={!editor.isActive('customImage') || !editor.getAttributes('customImage')['data-align']} title="Remove Image Alignment" > <ImageIcon className="h-4 w-4 mr-1" /> <Eraser className="h-3 w-3"/> </Button>

      <span className="border-r border-border mx-1"></span>

      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive('paragraph') ? 'is-active bg-primary/20' : ''} title="Paragraph"> <Pilcrow className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active bg-primary/20' : ''} title="Heading 1"> <Heading1 className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active bg-primary/20' : ''} title="Heading 2"> <Heading2 className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active bg-primary/20' : ''} title="Heading 3"> <Heading3 className="h-4 w-4" /> </Button>
      <span className="border-r border-border mx-1"></span>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active bg-primary/20' : ''} title="Bullet List"> <List className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active bg-primary/20' : ''} title="Ordered List"> <ListOrdered className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'is-active bg-primary/20' : ''} title="Blockquote"> <MessageSquareQuote className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule"> <Minus className="h-4 w-4" /> </Button>
      
      <span className="border-r border-border mx-1"></span>

      <Button type="button" variant="outline" size="sm" onClick={setLink} className={editor.isActive('link') ? 'is-active bg-primary/20' : ''} title="Add/Edit Link"> <LinkIcon className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={addImageByUrl} title="Add Image by URL"> <ImageIcon className="h-4 w-4" /> </Button>
      <Button type="button" variant="outline" size="sm" onClick={triggerImageUpload} title="Upload Image" disabled={isUploading}>
        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
      </Button>
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileSelectAndUpload} style={{ display: 'none' }} />

      <span className="border-r border-border mx-1"></span>
      <Button type="button" variant="outline" size="sm" onClick={() => { editor.chain().focus().unsetAllMarks().run(); editor.chain().focus().clearNodes().run(); }} title="Clear Formatting and Content of Nodes"> <Eraser className="h-4 w-4" /> </Button>
    </div>
  );
};

const RichTextEditor = ({ value, onChange }: { value: string; onChange: (htmlContent: string) => void }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      CustomImage.configure({ 
        inline: false,
        allowBase64: false,
      }),
      Superscript,
      Subscript,
      TextAlign.configure({ 
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
    ],
    content: value,
    onUpdate: ({ editor }: { editor: any }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose dark:prose-invert prose-sm sm:prose-base max-w-none focus:outline-none border border-input border-t-0 rounded-b-md p-3 min-h-[250px] bg-background',
      },
    },
  });

  return (
    <div className="border border-input rounded-md shadow-sm">
      <MenuBar editor={editor} />
      {editor && (
        <BubbleMenu 
            editor={editor} 
            tippyOptions={{ duration: 100, placement: 'top-start' }} 
            className="bg-background border border-border shadow-xl rounded-md p-1 flex gap-1"
        >
          <Button size="sm" type="button" variant="ghost" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active bg-primary/10' : ''}> Bold </Button>
          <Button size="sm" type="button" variant="ghost" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active bg-primary/10' : ''}> Italic </Button>
          <Button size="sm" type="button" variant="ghost" onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active bg-primary/10' : ''}> Strike </Button>
          {editor.isActive('customImage') && (
            <>
              <span className="border-r border-input mx-1 self-stretch"></span>
              <Button size="sm" type="button" variant="ghost" onClick={() => editor.commands.setImageAlignment('left')} className={editor.isActive('customImage', {'data-align': 'left'}) ? 'is-active bg-primary/10' : ''} title="Align Left">
                <AlignLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" type="button" variant="ghost" onClick={() => editor.commands.setImageAlignment('center')} className={editor.isActive('customImage', {'data-align': 'center'}) ? 'is-active bg-primary/10' : ''} title="Align Center">
                <AlignCenter className="h-4 w-4" />
              </Button>
              <Button size="sm" type="button" variant="ghost" onClick={() => editor.commands.setImageAlignment('right')} className={editor.isActive('customImage', {'data-align': 'right'}) ? 'is-active bg-primary/10' : ''} title="Align Right">
                <AlignRight className="h-4 w-4" />
              </Button>
               <Button size="sm" type="button" variant="ghost" onClick={() => editor.commands.setImageAlignment(null)} disabled={!editor.getAttributes('customImage')['data-align']} title="Align Default">
                <Eraser className="h-4 w-4" />
              </Button>
            </>
          )}
        </BubbleMenu>
      )}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;